import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Document, Image, Page, pdf, View } from '@react-pdf/renderer';
import { Upload, X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { EXPORT_OPTIONS, IMAGE_RESOLUTIONS } from '../constants/export-settings';
import { getMilimetersFromPixels } from '../utils/getMilimetersFromPixel';

export interface ExportImageRenderingModalProps {
  imgRef: React.RefObject<HTMLImageElement>;
}

const resolutionOptions = [
  {
    value: IMAGE_RESOLUTIONS.HD,
    label: '1920 x 1080 px',
    width: 1920,
    height: 1080,
  },
  {
    value: IMAGE_RESOLUTIONS['4K'],
    label: '3840 x 2160 px',
    width: 3920,
    height: 2160,
  },
  {
    value: IMAGE_RESOLUTIONS['8K'],
    label: '7680 x 4320 px',
    width: 7680,
    height: 4320,
  },
];

export const ExportImageRenderingModal = (props: ExportImageRenderingModalProps) => {
  const { imgRef } = props;
  const { t } = useTranslation();

  const canvasRef = useRef(null);
  const [resolution, setResolution] = useState<IMAGE_RESOLUTIONS | undefined>();
  const [exportAs, setExportAs] = useState<EXPORT_OPTIONS | undefined>();
  const [isExporting, setIsExporting] = useState(false);

  const exportOptions = [EXPORT_OPTIONS.PDF, EXPORT_OPTIONS.PNG];

  const selectedResolution = useMemo(
    () => resolutionOptions.find(({ value }) => value === resolution),
    [resolution]
  );

  const handleExport = async () => {
    setIsExporting(true);
    const canvas = canvasRef.current;

    if (canvas !== null && selectedResolution) {
      if (exportAs === EXPORT_OPTIONS.PDF) {
        const imgData = canvas.toDataURL('image/png');
        await exportPdf(imgData);
      }

      if (exportAs === EXPORT_OPTIONS.PNG) {
        const link = document.createElement('a');
        link.download = `exported-image.${exportAs}`;
        link.href = canvas.toDataURL();
        link.click();
      }
    }
    setIsExporting(false);
  };

  const exportPdf = async (imgData: any) => {
    try {
      const { width: imgWidth, height: imgHeight } = selectedResolution;
      const pdfWidth = getMilimetersFromPixels(imgWidth);
      const pdfHeight = getMilimetersFromPixels(imgHeight);

      const blob = await pdf(
        <Document>
          <Page wrap={false} size={{ width: pdfWidth + 'mm', height: pdfHeight + 'mm' }}>
            <View>
              <Image src={imgData} />
            </View>
          </Page>
        </Document>
      ).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exported-image.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url); // Clean up
      document.body.removeChild(a); // Clean up
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  useEffect(() => {
    const canvas = canvasRef?.current;

    if (canvas !== null && selectedResolution && imgRef !== null) {
      const { width, height } = selectedResolution;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(imgRef, 0, 0, width, height);

      const ratio = width / 1920;
      const lines = [t('virtualRednering'), t('disclaimerText')];
      const textWidth =
        lines.reduce((maxWidth, line) => {
          const width = ctx.measureText(line).width;
          return Math.max(maxWidth, width);
        }, 0) * ratio;
      const textHeight = 30 * ratio;
      const xOffset = 20 * ratio;
      const yOffset = 10 * ratio;

      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      const x = width - textWidth - 30 * ratio;
      const y = height - textHeight - 20 * ratio;
      const radius = 10 * ratio;

      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + textWidth + xOffset - radius, y);
      ctx.arcTo(x + textWidth + xOffset, y, x + textWidth + xOffset, y + radius, radius);
      ctx.lineTo(x + textWidth + xOffset, y + textHeight + yOffset - radius);
      ctx.arcTo(
        x + textWidth + xOffset,
        y + textHeight + yOffset,
        x + textWidth + xOffset - radius,
        y + textHeight + yOffset,
        radius
      );
      ctx.lineTo(x + radius, y + textHeight + yOffset);
      ctx.arcTo(x, y + textHeight + yOffset, x, y + textHeight + yOffset - radius, radius);
      ctx.lineTo(x, y + radius);
      ctx.arcTo(x, y, x + radius, y, radius);
      ctx.closePath();
      ctx.fill();

      const fontSize = 11 * ratio;
      ctx.font = `${fontSize}px Open Sans`;
      ctx.fillStyle = 'black';
      lines.forEach((line, index) => {
        ctx.fillText(line, x + fontSize, y + (index + 1) * (fontSize + 1) + 5 * ratio);
      });
    }
  }, [t, selectedResolution, imgRef, canvasRef]);

  return (
    <ModalBase className="flex min-h-[600px] w-full max-w-[900px] flex-col overflow-hidden rounded-sm p-0">
      <div className="flex h-[60px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6">
        <span className="text-sm font-semibold text-white">{t('image-rendering')}</span>
        <X className="cursor-pointer stroke-[4px] text-white" onClick={closeModal} />
      </div>
      <div className="flex flex-1 flex-col bg-white px-5 py-6">
        <div className="flex flex-1 items-start gap-3">
          <div className="flex w-full max-w-[230px] flex-col gap-2">
            <span className="block h-[40px] w-full content-center rounded-sm bg-text-primary px-5 text-sm font-semibold text-white">
              {t('image-resolution')}
            </span>
            {resolutionOptions.map((option) => (
              <div
                key={`resolution-option-${option.value}`}
                className={cn(
                  'flex h-[38px] cursor-pointer items-center justify-between rounded-sm px-4',
                  resolution === option.value ? 'bg-primary-light' : 'bg-[#EFF4F5]'
                )}
                onClick={() => setResolution(option.value)}
              >
                <span
                  className={cn(
                    'text-sm font-medium',
                    resolution === option.value ? 'text-white' : 'text-text-primary'
                  )}
                >
                  {option.value}
                </span>
                <span
                  className={cn(
                    'text-[10px] font-medium',
                    resolution === option.value ? 'text-white' : 'text-text-primary'
                  )}
                >
                  {option.label}
                </span>
              </div>
            ))}
            <span className="block h-[40px] w-full content-center rounded-sm bg-text-primary px-5 text-sm font-semibold text-white">
              {t('exportAs')}
            </span>
            {exportOptions.map((option) => (
              <div
                key={`export-as-option-${option}`}
                className="flex h-[38px] cursor-pointer items-center gap-4 rounded-sm bg-[#EFF4F5] px-3"
                onClick={() => setExportAs(option)}
              >
                <div className="h-[22px] w-[22px] rounded-full border-2 border-solid border-primary-light bg-white p-[2px]">
                  {exportAs === option && (
                    <div className="h-full w-full rounded-full bg-primary-light" />
                  )}
                </div>
                <span className="text-sm font-medium uppercase text-text-primary">{option}</span>
              </div>
            ))}
          </div>
          <div className="flex-1">
            {selectedResolution ? (
              <canvas
                ref={canvasRef}
                className="h-[350px] w-full rounded-sm border border-solid border-text-primary"
                width={selectedResolution.width}
                height={selectedResolution.height}
              />
            ) : null}
            <span className="mt-3 text-[10px] font-medium text-text-primary">
              {t('thisIsALowQualityPreview')}
            </span>
          </div>
        </div>
        <div className="flex items-end justify-end">
          <Button
            className="w-[200px] gap-2"
            disabled={!resolution || !exportAs || isExporting}
            onClick={handleExport}
          >
            {isExporting ? <Spinner /> : <Upload />}
            <span className="text-xl font-semibold">{t('export')}</span>
          </Button>
        </div>
      </div>
    </ModalBase>
  );
};
